import { Hidden } from '@mui/material'
import { omitBy } from 'lodash'
import { GetStaticProps, NextPage } from 'next'
import { useRouter } from 'next/router'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ContentPagination } from 'src/components/ContentPagination/ContentPagination'
import { Footer } from 'src/components/Footer/Footer'
import MainContainer from 'src/components/global/MainContainer/MainContainer'
import { PremiumModal } from 'src/components/global/PremiumModal'
import { SearchField } from 'src/components/global/SearchField'
import { Sidebar } from 'src/components/global/Sidebar'
import { ThemeOnly } from 'src/components/global/ThemeOnly'
import { IltaHeader } from 'src/components/IltaHeader/IltaHeader'
import { ReCaptcha } from 'src/components/ReCaptcha/ReCaptcha'
import { SearchPagination } from 'src/components/SearchPagination/SearchPagination'
import { SolutionSearchPageTab } from 'src/components/Tabs/SolutionSearchPageTab'
import { THEME } from 'src/config/config'
import { useGetTopicsForHeaderQuery, useSearchContentsLazyQuery } from 'src/graphql/generated/hooks'
// import { useAuthenticated } from 'src/hooks/useAuthenticated'
import { useUpdateQueryParams } from 'src/hooks/useUpdateQueryParams'
import { Meta } from 'src/layout/Meta'
import { Main } from 'src/templates/Main'
import { FilterOption, SEARCH_CONTENTS_TAB, SEARCH_DIRECTORY_TAB, TaxonomyType } from 'src/types'
import { getContentFilters, getDirectoryFilters } from 'src/utils/getFilters'

type Props = {
  filters: FilterOption[]
  contentFilters: FilterOption[]
}

const SolutionListPage: NextPage<Props> = ({ filters, contentFilters }) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [initialFilters, setInitialFilters] = useState<FilterOption[]>(filters)

  const router = useRouter()
  const lastUpdatedKeyword = useRef<string | null>(null)
  const { updateQueryParams } = useUpdateQueryParams()
  const tabs = router.query.tabs
  const pathName = tabs ? tabs[0] : 'directory'

  const [loadContents, { data: contentsData }] = useSearchContentsLazyQuery()

  const fromPage = (router?.query?.fromPage as string) || ''
  // Add state to track if content data is available
  const [hasContentData, setHasContentData] = useState(true)
  const [contentCount, setContentCount] = useState(0)

  const onSelectTab = (index: number) => {
    setTabIdx(index)
    let newTabs = 'directory'
    let defaultSort = 'Default'
    if (index === 401) {
      newTabs = 'directory'
      defaultSort = 'Default'
    }
    if (index === 402) {
      newTabs = 'contents'
      defaultSort = 'Chronologically'
    }
    const keyword = router.query.keyword
    updateQueryParams({
      tabs: newTabs,
      sort: defaultSort,
      page: undefined,
      keyword: (keyword as string | undefined) || undefined,
    })
  }

  // Add useEffect to check content data availability
  useEffect(() => {
    // Check if we should hide contents tab based on fromPage parameter
    const shouldHideContents = fromPage === 'home' || fromPage === 'solutionDetails'

    if (shouldHideContents) {
      // If fromPage is home or solutionDetails, we need to check if content data exists
      // This will be updated when ContentPagination component loads data
      setHasContentData(false) // Initially assume no content
    } else {
      setHasContentData(true) // Show contents tab for other cases
    }
  }, [fromPage])
  useEffect(() => {
    ;(async () => {
      if (!hasContentData) {
        const filters = router.query
        const query = {
          offset: 0,
          limit: 10,
          practiceAreas: filters?.practiceAreas || undefined,
        }
        loadContents({ variables: { query: omitBy(query, (value) => !value) } })
      }
    })()
  }, [hasContentData, router])

  useEffect(() => {
    if (contentsData) {
      setContentCount(contentsData.contentResults?.data?.total || 0)

      const tabs = router.query.tabs
      const tabName = tabs ? tabs : 'directory'

      if (tabName === 'contents' && !hasContentData && contentsData?.contentResults?.data?.total == 0) {
        onSelectTab(401)
      }
    }
  }, [contentsData])

  const meta = (
    <Meta
      title="Search legaltech products"
      description="Search legaltech products"
      url={`/solution-list/${pathName}`}
    />
  )
  const [showModal, setShowModal] = useState(false)
  const [tabIdx, setTabIdx] = useState(SEARCH_DIRECTORY_TAB)
  const [filterHeight, setFilterHeight] = useState(false)
  // const { session } = useAuthenticated()
  // const filtersDisabled = useMemo(() => !session?.user?.isPremium, [session])

  // Get search keyword from query parameters for highlighting (reactive to router changes)
  const searchKeyword = useMemo(() => {
    if (!router.isReady) return undefined
    const keyword = router.query.keyword
    if (!keyword) return undefined

    let keywordStr: string
    if (Array.isArray(keyword)) {
      keywordStr = keyword[0] || ''
    } else {
      keywordStr = keyword as string
    }

    // Decode URI component in case it's URL encoded (handles + as space, %20, etc.)
    try {
      return decodeURIComponent(keywordStr)
    } catch {
      // If decoding fails, return as is
      return keywordStr || undefined
    }
  }, [router.isReady, router.query.keyword])

  const { data } = useGetTopicsForHeaderQuery({ fetchPolicy: 'cache-first' })

  const isSponsorLevel = initialFilters.some((item, index) => {
    if (item.title === TaxonomyType.SponsorLevel) {
      initialFilters[index].data = initialFilters[index].data.sort((a, b) => {
        if (a.level && b.level) {
          return a.level - b.level
        }
        return 0
      })
    }
    return item.title === TaxonomyType.SponsorLevel
  })

  if (THEME === 'ilta') {
    return null
  }
  // Add this useEffect to the SolutionListPage component
  useEffect(() => {
    if (THEME === 'ilta') {
      router.push('/')
    }
  }, [router])

  useEffect(() => {
    const { tabs } = router.query
    if (tabs && tabs === 'directory') {
      setTabIdx(SEARCH_DIRECTORY_TAB)
    }
    if (tabs && tabs === 'contents') {
      setTabIdx(SEARCH_CONTENTS_TAB)
    }
  }, [router.isReady])

  useEffect(() => {
    if (!data || !router.query.keyword) return

    const keyword = decodeURIComponent(router.query.keyword as string).toLowerCase()

    if (lastUpdatedKeyword.current?.toLowerCase?.() === keyword && data) return

    const productType: string[] = []

    if (data?.topics.some((item) => item?.name.toLowerCase() === keyword)) {
      data.topics
        .filter((item) => item?.name.toLowerCase() === keyword)
        .forEach((item) => {
          item?.subTopics.forEach((subTopic) => {
            productType.push(item.documentId + '@@@@' + subTopic?.documentId)
          })
        })

      updateQueryParams({ productType: productType.join(',') })
    } else {
      data?.topics.forEach((item) => {
        item?.subTopics
          .filter((sub) => sub?.name.toLowerCase() === keyword)
          .forEach((sub) => {
            updateQueryParams({
              productType: item.documentId + '@@@@' + sub?.documentId,
            })
          })
      })
    }

    lastUpdatedKeyword.current = keyword
  }, [router.query.keyword, data])

  return (
    <ReCaptcha>
      <Main meta={meta}>
        <MainContainer id="mainContainer">
          <ThemeOnly theme="ilta">
            <IltaHeader />
          </ThemeOnly>
          <div className="flex flex-col items-center justify-center bg-clouds px-5 md:px-0">
            <div className="!max-w-[1100px] w-full py-2">
              <h1 className="pt-[0.5rem] pb-[0.625rem] text-[24px] md:text-[30px] font-bold font-secondary text-darkgray">
                {tabIdx === SEARCH_DIRECTORY_TAB && 'Search Results'}
                {tabIdx === SEARCH_CONTENTS_TAB && 'Search Contents'}
              </h1>
            </div>
            <ThemeOnly theme="legaltech">
              <div className="!max-w-[1100px] w-full py-2">
                <div className="md:w-1/4 pr-10">
                  <SearchField className="mb-[23px]" />
                </div>
              </div>
            </ThemeOnly>
          </div>
          <ThemeOnly theme="legaltech">
            <div className="flex w-full bg-clouds justify-center items-center">
              <div className="!max-w-[1100px] w-full flex flex-row justify-center items-center">
                <SolutionSearchPageTab
                  id={SEARCH_DIRECTORY_TAB}
                  activeTab={tabIdx}
                  className="w-[117px]"
                  onSelectTab={onSelectTab}
                >
                  Directory
                </SolutionSearchPageTab>
                {!hasContentData && contentCount > 0 && (
                  <SolutionSearchPageTab
                    id={SEARCH_CONTENTS_TAB}
                    activeTab={tabIdx}
                    className="w-[117px]"
                    onSelectTab={onSelectTab}
                  >
                    Contents
                  </SolutionSearchPageTab>
                )}
                {hasContentData && (
                  <SolutionSearchPageTab
                    id={SEARCH_CONTENTS_TAB}
                    activeTab={tabIdx}
                    className="w-[117px]"
                    onSelectTab={onSelectTab}
                  >
                    Contents
                  </SolutionSearchPageTab>
                )}
              </div>
            </div>
          </ThemeOnly>
          <hr />
          {tabIdx === SEARCH_DIRECTORY_TAB && (
            <div className="flex flex-row justify-center w-full md:px-0 px-5">
              <div className="!max-w-[1100px] flex flex-row justify-between w-full mb-32 relative">
                <Hidden mdDown>
                  <div className="w-1/4 pr-10 flex flex-col">
                    <ThemeOnly theme="ilta">
                      <SearchField className="my-8" />
                    </ThemeOnly>
                    <Sidebar
                      filterOptions={initialFilters}
                      defaultSelectFilterIdx={isSponsorLevel ? 3 : 1}
                      filterHeight={filterHeight}
                    />
                  </div>
                </Hidden>
                <SearchPagination
                  filterProps={initialFilters}
                  followOnly={false}
                  includeCompany={true}
                  setFilterHeight={setFilterHeight}
                  showExportButton={true}
                />
              </div>
            </div>
          )}

          {tabIdx === SEARCH_CONTENTS_TAB && (
            <div className="flex flex-row justify-center w-full md:pl-0 md:pr-0 pl-[20px] pr-[20px]">
              <div className="!max-w-[1100px] flex flex-row justify-between w-full mb-32">
                <Hidden mdDown>
                  <div className="w-1/4 pr-10 flex flex-col">
                    <Sidebar
                      filterOptions={contentFilters}
                      // filtersDisabled={filtersDisabled}
                      defaultSelectFilterIdx={1}
                      onClickWhenDisabled={() => setShowModal(true)}
                    />
                  </div>
                </Hidden>
                <PremiumModal isOpen={showModal} onClose={() => setShowModal(false)} />
                <ContentPagination
                  filterProps={contentFilters}
                  // filtersDisabled={filtersDisabled}
                  onClickWhenDisabled={() => setShowModal(true)}
                  searchKeyword={searchKeyword}
                />
              </div>
            </div>
          )}

          <ThemeOnly theme="legaltech">
            <Footer />
          </ThemeOnly>
        </MainContainer>
      </Main>
    </ReCaptcha>
  )
}

export default SolutionListPage

export const getStaticProps: GetStaticProps<Props> = async ({}) => {
  try {
    const filters = await getDirectoryFilters()

    const contentFiltersOptions = {
      subjectsFilter: { includeSubjectMatters: true, includeThemes: false },
      authorsFilter: { includeAuthors: true, includeExperts: true },
      practiceAreasFilter: true,
    }
    const contentFilters: FilterOption[] = await getContentFilters(contentFiltersOptions)

    return {
      props: {
        filters,
        contentFilters,
      },
      revalidate: 60,
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error occurred in getStaticProps of Search:', error)
    // Graceful fallback in case of an upstream error
    return {
      notFound: true,
      revalidate: 5, // Reattempt fetching during the next static regeneration
    }
  }
}

/* export async function getStaticPaths() {
  return {
    paths: [
      // String variant:
      // '/search/',
      // '/search/directory',
      // '/search/contents',
      // Object variant:
      { params: { tabs: [] } },
      { params: { tabs: ['directory'] } },
      { params: { tabs: ['contents'] } },
    ],
    fallback: false,
  }
} */
